import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { Search, Loader2, MapPin, User } from 'lucide-react';
import BottomNav from '../components/BottomNav';

const S = {
  screen: {
    minHeight: '100vh',
    background: '#001a4d',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: "'Inter', sans-serif",
    paddingBottom: '80px',
  },
  header: {
    background: 'rgba(255,255,255,0.06)',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    padding: '14px 18px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  avatar: {
    width: '38px',
    height: '38px',
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  officerName: {
    fontFamily: "'Oswald', sans-serif",
    fontSize: '15px',
    fontWeight: 600,
    color: 'white',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    marginBottom: '2px',
  },
  officerSite: {
    fontSize: '11px',
    color: 'rgba(255,255,255,0.5)',
    display: 'flex',
    alignItems: 'center',
    gap: '3px',
  },
  siaPill: {
    background: 'rgba(255,255,255,0.1)',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '20px',
    padding: '4px 10px',
    fontFamily: "'Oswald', sans-serif",
    fontSize: '8px',
    color: 'rgba(255,255,255,0.8)',
    fontWeight: 600,
    letterSpacing: '1.5px',
    marginLeft: 'auto',
  },
  body: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '32px 20px',
  },
  title: {
    fontFamily: "'Oswald', sans-serif",
    fontSize: '42px',
    fontWeight: 700,
    color: 'white',
    letterSpacing: '8px',
    textTransform: 'uppercase',
    marginBottom: '4px',
    textAlign: 'center',
  },
  subtitle: {
    fontSize: '11px',
    color: 'rgba(255,255,255,0.45)',
    marginBottom: '32px',
    textAlign: 'center',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    fontFamily: "'Oswald', sans-serif",
  },
  card: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px 20px',
    width: '100%',
    maxWidth: '360px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
  },
  fieldLabel: {
    fontFamily: "'Oswald', sans-serif",
    fontSize: '10px',
    fontWeight: 600,
    letterSpacing: '3px',
    color: '#7a8aaa',
    textTransform: 'uppercase',
    marginBottom: '8px',
  },
  plate: {
    background: '#F5C400',
    border: '3px solid #111',
    borderRadius: '8px',
    width: '100%',
    height: '64px',
    fontSize: '30px',
    fontFamily: "'Oswald', sans-serif",
    fontWeight: 700,
    color: '#111',
    letterSpacing: '7px',
    textAlign: 'center',
    outline: 'none',
    marginBottom: '16px',
    display: 'block',
  },
  checkBtn: {
    width: '100%',
    background: '#001a4d',
    border: 'none',
    borderRadius: '10px',
    padding: '16px',
    color: 'white',
    fontFamily: "'Oswald', sans-serif",
    fontSize: '18px',
    fontWeight: 600,
    letterSpacing: '5px',
    textTransform: 'uppercase',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    cursor: 'pointer',
    boxShadow: '0 4px 16px rgba(13,27,62,0.4)',
    marginBottom: '16px',
    transition: 'opacity 0.2s',
  },
  notice: {
    background: '#fffbf0',
    border: '1px solid #f0d890',
    borderLeft: '3px solid #C9973A',
    borderRadius: '8px',
    padding: '10px 12px',
  },
  noticeTitle: {
    fontFamily: "'Oswald', sans-serif",
    fontSize: '9px',
    fontWeight: 600,
    letterSpacing: '2px',
    color: '#a07020',
    textTransform: 'uppercase',
    marginBottom: '4px',
  },
  noticeBody: {
    fontSize: '10px',
    color: '#907030',
    lineHeight: 1.6,
  },
  error: {
    background: '#fff5f5',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    padding: '10px',
    textAlign: 'center',
    color: '#dc2626',
    fontSize: '13px',
    marginBottom: '12px',
  },
};

export default function HomeScreen() {
  const navigate = useNavigate();
  const { user, fetchUser } = useAuth();

  const [vrn, setVrn] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [officerData, setOfficerData] = useState(null);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const data = await fetchUser();
        setOfficerData(data);
      } catch (err) {
        console.error('Failed to load profile:', err);
      }
    };
    loadProfile();
  }, [fetchUser]);

  const handleVRNChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/\s/g, '');
    setVrn(value);
    setError(null);
  };

  const handleCheck = async (e) => {
    e.preventDefault();
    if (!vrn.trim()) { setError('Please enter a VRN'); return; }
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/vehicles/check?vrn=${encodeURIComponent(vrn)}`);
      if (response.data.known_vehicle?.status === 'BANNED') {
        navigate('/banned-vehicle', {
          state: {
            vrn: response.data.vrn || vrn,
            notes: response.data.known_vehicle.notes || null,
            controlCentreNumber: response.data.known_vehicle.control_centre_number || null,
          },
        });
        return;
      }
      navigate('/result', { state: { result: response.data, vrn } });
    } catch (err) {
      if (err.code === 'INVALID_VRN') {
        setError('Invalid VRN format');
      } else {
        setError(err.message || 'Failed to check VRN. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const officerName = officerData?.full_name || user?.email || 'Officer';
  const siteName = officerData?.assigned_sites?.[0]?.name || 'Assigned Site';

  return (
    <div style={S.screen} data-testid="home-screen">

      {/* Header */}
      <div style={S.header}>
        <div style={S.avatar}>
          <User size={18} color="white" />
        </div>
        <div style={{ flex: 1 }}>
          <div style={S.officerName} data-testid="officer-name">{officerName}</div>
          <div style={S.officerSite}>
            <MapPin size={11} color="rgba(255,255,255,0.5)" />
            <span data-testid="officer-site">{siteName}</span>
          </div>
        </div>
        <div style={S.siaPill}>SIA VERIFIED</div>
      </div>

      {/* Body */}
      <div style={S.body}>
        <div style={S.title}>VCIN</div>
        <div style={S.subtitle}>Vehicle Crime Intelligence Network</div>

        <div style={S.card}>
          <form onSubmit={handleCheck}>
            {error && <div style={S.error} data-testid="vrn-error">{error}</div>}

            <div style={S.fieldLabel}>Enter Registration</div>

            <input
              type="text"
              value={vrn}
              onChange={handleVRNChange}
              placeholder="AB12 CDE"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck="false"
              maxLength={8}
              style={{
                ...S.plate,
                color: vrn ? '#111' : 'rgba(0,0,0,0.22)',
              }}
              data-testid="vrn-input"
            />

            <button
              type="submit"
              disabled={loading}
              style={{ ...S.checkBtn, opacity: loading ? 0.7 : 1 }}
              data-testid="check-btn"
            >
              {loading ? (
                <><Loader2 size={20} className="animate-spin" /><span>CHECKING...</span></>
              ) : (
                <><Search size={20} /><span>CHECK VEHICLE</span></>
              )}
            </button>

            <div style={S.notice} data-testid="cautionary-notice">
              <div style={S.noticeTitle}>⚠ Authorised Use Only</div>
              <div style={S.noticeBody}>
                SIA licensed personnel only. All checks are logged and monitored. Misuse may result in immediate account suspension.
                Vehicle checks may return personal data about the registered keeper. Use only for lawful security purposes.
                {' '}<a href="/privacy" style={{ color: '#4a7aff', textDecoration: 'underline' }}>Privacy Notice</a>
              </div>
            </div>
          </form>
        </div>
      </div>

      <BottomNav activeTab="home" />
    </div>
  );
}
